import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQuery, useApolloClient } from '@apollo/client';
import { format } from 'date-fns';
import { Tabs, Tab } from '@mui/material';

// GraphQL imports
import { 
  UPDATE_JOB, 
  UPDATE_JOB_STATUS, 
  RESTORE_JOB, 
  UPDATE_JOB_SCHEDULED_DATE, 
  ASSIGN_JOBS_TO_TEAM,
  MANUALLY_CANCEL_JOB
} from '../../graphql/mutations';
import { 
  GET_USERS, 
  GET_JOB_FOR_EDIT, 
  GET_JOBS_BY_RESERVATION_ID, 
  GET_PREVIOUS_RESERVATION, 
  GET_NEXT_RESERVATION, 
  GET_SERVICE_TYPES,
  GET_PROPERTY_RESERVATIONS,
  GET_JOBS,
  GET_MY_SCHEDULE_JOBS
} from '../../graphql/queries';
import { GET_ACTIVE_TEAMS } from '../../graphql/teams';

// Utility imports
import { getCheckoutTime } from '../../utils/job-utils';
import { useAuth } from '../../contexts/AuthContext';
import { getAssignableUsersForServiceType, canCancelRestoreJobs, canViewFinancials } from '../../utils/role-utils';

// Component imports
import JobEditForm from './components/JobEditForm';
import ReservationEditForm from './components/ReservationEditForm';
import CustomFlagManager from '../custom-flags/CustomFlagManager';

// Hook and utility imports
import { useReservationEdit } from './hooks/useReservationEdit';
import { 
  TABS, 
  ASSIGNMENT_TYPES, 
  REQUIRED_ROLES
} from './constants/editJobConstants';
import { 
  hasRequiredRole, 
  getSubmitButtonText 
} from './utils/editJobUtils';
import { invalidateAssignmentCaches } from '../../utils/cache-utils';
import { mergeServerJobIntoForm } from './utils/jobFormState';

const EditJobModal = ({ job, onClose, onUpdate, onCopyJob }) => {
  const { user, loading } = useAuth();
  const client = useApolloClient();
  
  // Authentication checks
  const userHasRequiredRole = hasRequiredRole(user?.role, REQUIRED_ROLES);

  // Tab state
  const [activeTab, setActiveTab] = useState(TABS.JOB);
  const [affectedJobsCount, setAffectedJobsCount] = useState(0);

  // Assignment state
  const [assignmentType, setAssignmentType] = useState(
    job.assignedTeamId ? ASSIGNMENT_TYPES.TEAM : ASSIGNMENT_TYPES.INDIVIDUAL
  );
  const [selectedTeamId, setSelectedTeamId] = useState(
    job.assignedTeamId ? String(job.assignedTeamId) : ''
  );
  
  // Track if user has manually changed assignment type to prevent auto-reset
  const [userChangedAssignmentType, setUserChangedAssignmentType] = useState(false);

  // Job data processing
  const jobCheckoutTime = getCheckoutTime(job) || '';

  // Form state - Initialize with job prop data, will be updated when fresh job data loads
  const [formData, setFormData] = useState({
    assignedToId: job.assignedTo?.id ? String(job.assignedTo.id) : '', // Use job prop as initial value
    serviceTypeId: job.serviceType?.id || '',
    status: job.status || 'PENDING',
    notes: job.notes || '',
    scheduledDate: format(new Date(job.scheduledDate), "yyyy-MM-dd"),
    startTime: job.startTime || jobCheckoutTime || '',
    hasDateOverride: job.hasDateOverride || false, // Initialize from job data
    hasTimeOverride: job.hasTimeOverride || false, // Initialize from job data
    hasServiceTypeOverride: job.hasServiceTypeOverride || false,
    flipOverridden: job.flipOverridden || false,
    flipOverrideValue: job.flipOverrideValue !== undefined ? job.flipOverrideValue : false,
    payout: job.payout !== null && job.payout !== undefined ? job.payout.toString() : '',
    billedAmount: job.billedAmount !== null && job.billedAmount !== undefined ? job.billedAmount.toString() : '',
    showFinancialFields: true
  });
  const dirtyFieldsRef = useRef(new Set());
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);

  const setUserFormData = (updater) => {
    setFormData(previousForm => {
      const nextForm = typeof updater === 'function' ? updater(previousForm) : updater;
      Object.keys(nextForm).forEach(field => {
        if (!Object.is(previousForm[field], nextForm[field])) {
          dirtyFieldsRef.current.add(field);
        }
      });
      return nextForm;
    });
  };

  // Update assignment state when job prop changes - only set assignment type, not assignment values
  useEffect(() => {
    if (job) {
      dirtyFieldsRef.current = new Set();
      setAssignmentType(job.assignedTeamId ? ASSIGNMENT_TYPES.TEAM : ASSIGNMENT_TYPES.INDIVIDUAL);
      setSelectedTeamId(job.assignedTeamId ? String(job.assignedTeamId) : '');
    }
  }, [job.id]); // Only run when job ID changes (modal opens with new job)

  // Debug state
  const [debugInfo, setDebugInfo] = useState({
    attempts: 0,
    lastError: '',
    lastResponse: ''
  });
  
  // Processing state to prevent double submissions
  const [isProcessingDateUpdate, setIsProcessingDateUpdate] = useState(false);

  // PERFORMANCE OPTIMIZATION: Only load data when user is authenticated
  // Note: Modal is conditionally rendered, so if this component exists, it's "open"
  const shouldLoadData = user && !loading;
  
  // GraphQL queries - only run when modal is open and user is authenticated
  const { data: usersData, loading: usersLoading } = useQuery(GET_USERS, {
    skip: !shouldLoadData, // Only load when modal is open
    fetchPolicy: 'cache-first' // Use cache-first for better performance
  });
  const { data: serviceTypesData } = useQuery(GET_SERVICE_TYPES, {
    fetchPolicy: 'cache-first', // Use cache-first instead of network-only
    skip: !shouldLoadData, // Only load when modal is open
    onError: (error) => {
      // Error handled silently
    }
  });
  const { data: teamsData, loading: teamsLoading } = useQuery(GET_ACTIVE_TEAMS, {
    skip: !shouldLoadData, // Only load when modal is open
    fetchPolicy: 'cache-first' // Use cache-first for better performance
  });

  const { data: jobData, loading: jobDataLoading, error: jobDataError } = useQuery(GET_JOB_FOR_EDIT, {
    variables: { id: job.id },
    fetchPolicy: 'network-only', // CRITICAL FIX: Use network-only to get fresh assignment data
    skip: !shouldLoadData || !job?.id, // Only load when modal is open
    onCompleted: (data) => {
      // Data loaded successfully
    },
    onError: (error) => {
      // Error handled silently
    }
  });

  // PERFORMANCE OPTIMIZATION: Only load reservation data when modal is open
  const shouldFetchReservations = shouldLoadData && job?.property?.id && job?.scheduledDate;
  const { data: previousReservationQueryData } = useQuery(GET_PREVIOUS_RESERVATION, {
    variables: {
      propertyId: job?.property?.id,
      date: job?.scheduledDate ? new Date(job.scheduledDate).toISOString() : undefined
    },
    skip: !shouldFetchReservations
    // 🚀 PERFORMANCE FIX: Use default cache-first for speed
  });

  const { data: nextReservationQueryData } = useQuery(GET_NEXT_RESERVATION, {
    variables: {
      propertyId: job?.property?.id,
      date: job?.scheduledDate ? new Date(job.scheduledDate).toISOString() : undefined
    },
    skip: !shouldFetchReservations
    // 🚀 PERFORMANCE FIX: Use default cache-first for speed  
  });

  useQuery(GET_JOBS_BY_RESERVATION_ID, {
    variables: { reservationId: job?.reservation?.id },
    skip: !shouldLoadData || !job?.reservation?.id || activeTab !== TABS.RESERVATION,
    fetchPolicy: 'cache-first', // Use cache-first for better performance
    onCompleted: (data) => {
      setAffectedJobsCount(data?.jobsByReservation?.length || 0);
    }
  });

  // Fetch current reservations for this property to get guest contact info - only when needed
  const { data: propertyReservationsData } = useQuery(GET_PROPERTY_RESERVATIONS, {
    variables: { propertyId: job?.property?.id },
    skip: !shouldLoadData || !job?.property?.id,
    fetchPolicy: 'cache-first', // Use cache-first instead of network-only
  });

  const previousReservation = previousReservationQueryData?.previousReservationForProperty;
  const nextReservation = nextReservationQueryData?.nextReservationForProperty;

  // Find the current or most recent reservation for guest contact info
  const getCurrentReservationWithContact = () => {
    // First, try to use the reservation from the job if it has contact info
    if (job.reservation?.email || job.reservation?.mobile_phone) {
      return job.reservation;
    }
    
    // Otherwise, find current or most recent reservation from property reservations
    const reservations = propertyReservationsData?.propertyReservations || [];
    const now = new Date();
    
    // Find current reservation (check-in <= now <= check-out)
    const currentReservation = reservations.find(res => {
      const checkIn = new Date(res.check_in);
      const checkOut = new Date(res.check_out);
      return checkIn <= now && now <= checkOut && !res.is_cancelled;
    });
    
    if (currentReservation) return currentReservation;
    
    // Find most recent past reservation with contact info
    const pastReservations = reservations
      .filter(res => new Date(res.check_out) < now && !res.is_cancelled && (res.email || res.mobile_phone))
      .sort((a, b) => new Date(b.check_out) - new Date(a.check_out));
    
    if (pastReservations.length > 0) return pastReservations[0];
    
    // Find next upcoming reservation with contact info
    const upcomingReservations = reservations
      .filter(res => new Date(res.check_in) > now && !res.is_cancelled && (res.email || res.mobile_phone))
      .sort((a, b) => new Date(a.check_in) - new Date(b.check_in));
    
    return upcomingReservations.length > 0 ? upcomingReservations[0] : null;
  };

  const currentReservationWithContact = getCurrentReservationWithContact();

  // Initialize reservation editing hook - pass contact info separately to preserve navigation
  const reservationEdit = useReservationEdit(job, previousReservation, nextReservation, onUpdate, onClose, currentReservationWithContact);

  // Data processing
  const users = usersData?.users || [];
  const serviceTypes = serviceTypesData?.serviceTypes || [];

  const availableServiceTypes = serviceTypes.filter(type => {
    if (!type.isActive) return false;
    if (['ADMIN', 'GUEST_APP_MANAGER'].includes(user?.role)) return true;
    return type.allowedRoles?.includes(user?.role);
  });

  // Add state to track if form has been initialized with fresh data
  const [isFormInitialized, setIsFormInitialized] = useState(false);

  const displayTime = formData.hasTimeOverride ? formData.startTime : jobCheckoutTime;

  // Get the current service type for filtering
  const currentServiceType = useMemo(() => {
    // Always try to find the service type from the GraphQL data first (which has allowedRoles)
    const serviceTypeId = formData.serviceTypeId || job.serviceType?.id;
    
    if (serviceTypeId) {
      // Find from the complete service types data (includes allowedRoles)
      const foundServiceType = serviceTypes.find(st => st.id === serviceTypeId);
      if (foundServiceType) {
        return foundServiceType;
      }
    }
    
    // Fallback to job's service type if not found in GraphQL data
    return job.serviceType;
  }, [formData.serviceTypeId, job.serviceType, serviceTypes]);

  const activeUsers = getAssignableUsersForServiceType(users, job?.property, currentServiceType);


  const sortedActiveUsers = useMemo(() => {
    if (!Array.isArray(activeUsers)) return [];
    
    return [...activeUsers].sort((a, b) => {
      const getFirstName = (user) => {
        if (user.firstName) return user.firstName.toLowerCase();
        if (user.name) return user.name.split(' ')[0].toLowerCase();
        return '';
      };
      
      return getFirstName(a).localeCompare(getFirstName(b));
    });
  }, [activeUsers]);

  // Mutations - keeping the complex logic here for now
  const [updateJob, { loading: updateJobLoading }] = useMutation(UPDATE_JOB, {
    onCompleted: (data) => {
      setDebugInfo(prev => ({
        ...prev,
        lastResponse: JSON.stringify(data, null, 2),
        attempts: prev.attempts + 1
      }));
      
      // Use proper assignment cache invalidation
      invalidateAssignmentCaches(client);
      
      // Also evict the specific job from cache to force fresh data
      client.cache.evict({ 
        id: client.cache.identify({ __typename: 'Job', id: job.id }) 
      });
      
      // Close modal after successful job update and trigger parent refetch
      if (onUpdate) {
        // Add a small delay to ensure cache invalidation completes
        setTimeout(() => {
          onUpdate();
        }, 100);
      }
      onClose();
    },
    onError: (error) => {
      setDebugInfo(prev => ({
        ...prev,
        lastError: error.message,
        attempts: prev.attempts + 1
      }));
    }
  });

  const [updateJobStatus, { loading: updateStatusLoading }] = useMutation(UPDATE_JOB_STATUS, {
    onCompleted: (data) => {
      setDebugInfo(prev => ({
        ...prev,
        lastResponse: JSON.stringify(data, null, 2),
        attempts: prev.attempts + 1
      }));
      
      // Force cache invalidation for all job-related queries
      try {
        invalidateAssignmentCaches(client);
      } catch (error) {
        // Error invalidating cache
      }
        
        if (onUpdate) {
          setTimeout(() => {
            onUpdate();
          }, 100);
        }
        onClose();
      },
      onError: (error) => {
      setDebugInfo(prev => ({
        ...prev,
        lastError: error.message,
        attempts: prev.attempts + 1
      }));
    },
    refetchQueries: ['GetJobsForAssignments', 'GetAssignedJobsForAssignments']
  });

  const [updateJobScheduledDate, { loading: updateDateLoading }] = useMutation(UPDATE_JOB_SCHEDULED_DATE, {
    onCompleted: async (data) => {
      setDebugInfo(prev => ({
        ...prev,
        lastResponse: JSON.stringify(data, null, 2),
        attempts: prev.attempts + 1
      }));
      
      // Reset processing flag
      setIsProcessingDateUpdate(false);
      
      try {
        // After date update, check if we also need to handle assignment changes
        const assignmentChanged = 
          (assignmentType === ASSIGNMENT_TYPES.TEAM && (!job.assignedTeamId || selectedTeamId !== String(job.assignedTeamId))) ||
          (assignmentType === ASSIGNMENT_TYPES.INDIVIDUAL && (job.assignedTeamId || 
            (formData.assignedToId === '' && job.assignedTo?.id) || // Unassigning from a user
            (formData.assignedToId !== '' && formData.assignedToId !== (job.assignedTo?.id ? String(job.assignedTo.id) : ''))));

        // Handle team assignment change
        if (assignmentType === ASSIGNMENT_TYPES.TEAM && assignmentChanged && selectedTeamId) {
          await assignJobsToTeam({
            variables: {
              jobIds: [job.id],
              teamId: parseInt(selectedTeamId, 10)
            }
          });
          return; // Team assignment will handle closing
        }

        // Check if we need to update other job fields (including individual assignment)
        const needsJobUpdate = 
          formData.hasTimeOverride !== (job.hasTimeOverride || false) ||
          (formData.hasTimeOverride && formData.startTime !== (job.startTime || '')) ||
          (formData.showFinancialFields && (formData.payout !== (job.payout || '') || formData.billedAmount !== (job.billedAmount || ''))) ||
          assignmentChanged ||
          formData.serviceTypeId !== (job.serviceType?.id || '') ||
          formData.hasServiceTypeOverride !== (job.hasServiceTypeOverride || false) ||
          formData.flipOverridden !== (job.flipOverridden || false) ||
          (formData.flipOverridden && formData.flipOverrideValue !== job.flipOverrideValue);
        


        if (needsJobUpdate) {
          // Prepare financial fields
          const financialFields = {};
          if (formData.showFinancialFields) {
            if (formData.payout !== '') {
              financialFields.payout = parseFloat(formData.payout);
            }
            if (formData.billedAmount !== '') {
              financialFields.billedAmount = parseFloat(formData.billedAmount);
            }
          }

          const startTimeToSend = formData.hasTimeOverride ? formData.startTime : null;
          const assignedToIdValue = assignmentType === ASSIGNMENT_TYPES.INDIVIDUAL
            ? (formData.assignedToId && formData.assignedToId !== '' ? parseInt(formData.assignedToId, 10) : null)
            : null;
          
          const serviceTypeFields = {};
          if (formData.serviceTypeId && formData.serviceTypeId !== (job.serviceType?.id || '')) {
            serviceTypeFields.serviceTypeId = formData.serviceTypeId;
            serviceTypeFields.hasServiceTypeOverride = formData.hasServiceTypeOverride;
          } else if (formData.hasServiceTypeOverride !== (job.hasServiceTypeOverride || false)) {
            serviceTypeFields.hasServiceTypeOverride = formData.hasServiceTypeOverride;
          }

          const flipOverrideFields = {};
          if (formData.flipOverridden !== (job.flipOverridden || false)) {
            flipOverrideFields.flipOverridden = formData.flipOverridden;
            if (formData.flipOverridden) {
              flipOverrideFields.flipOverrideValue = formData.flipOverrideValue;
            }
          } else if (formData.flipOverridden && formData.flipOverrideValue !== job.flipOverrideValue) {
            flipOverrideFields.flipOverrideValue = formData.flipOverrideValue;
          }
          
          await updateJob({
            variables: {
              id: job.id,
              input: {
                id: job.id,
                hasDateOverride: formData.hasDateOverride,
                hasTimeOverride: formData.hasTimeOverride,
                startTime: startTimeToSend,
                assignedToId: assignedToIdValue,
                ...(assignmentChanged && { hasAssignmentOverride: true }),
                notes: formData.notes,
                ...serviceTypeFields,
                ...flipOverrideFields,
                ...financialFields
              }
            }
          });
          
        // Handle status change if needed
        if (formData.status !== job.status) {
          // If status is being changed to CANCELLED, use manual cancellation
          if (formData.status === 'CANCELLED') {
            await manuallyCancelJob({
              variables: {
                id: job.id,
                reason: formData.notes || `Job manually cancelled by ${user?.name || 'Admin'} on ${format(new Date(), 'MMM d, yyyy')}`
              }
            });
          } else {
            await updateJobStatus({
              variables: {
                id: job.id,
                status: formData.status,
                notes: formData.notes
              }
            });
          }
        }
          return; // updateJob will handle closing
        }
        
        // Handle status/notes only changes
        if (formData.status !== job.status || formData.notes !== job.notes) {
          // If status is being changed to CANCELLED, use manual cancellation
          if (formData.status === 'CANCELLED' && formData.status !== job.status) {
            await manuallyCancelJob({
              variables: {
                id: job.id,
                reason: formData.notes || `Job manually cancelled by ${user?.name || 'Admin'} on ${format(new Date(), 'MMM d, yyyy')}`
              }
            });
          } else {
            updateJobStatus({
              variables: {
                id: job.id,
                status: formData.status,
                notes: formData.notes
              }
            });
          }
        } else {
          // Cache invalidation for scheduled date updates
          try {
            invalidateAssignmentCaches(client);
          } catch (error) {
            // Error invalidating cache
          }
          
          // Always close modal after successful date update, even if no other changes
          if (onUpdate) {
            setTimeout(() => {
              onUpdate();
            }, 100);
          }
          onClose();
        }
      } catch (error) {
        setDebugInfo(prev => ({
          ...prev,
          lastError: error.message
        }));
      }
    },
    onError: (error) => {
      setDebugInfo(prev => ({
        ...prev,
        lastError: error.message,
        attempts: prev.attempts + 1
      }));
      // Reset processing flag on error
      setIsProcessingDateUpdate(false);
    }
  });


  const [restoreJob, { loading: restoreJobLoading }] = useMutation(RESTORE_JOB, {
    onCompleted: (data) => {
      setDebugInfo(prev => ({
        ...prev,
        lastResponse: JSON.stringify(data, null, 2),
        attempts: prev.attempts + 1
      }));
      
      updateJobStatus({
        variables: {
          id: job.id,
          status: job.status,
          notes: `${formData.notes ? formData.notes + '\\n\\n' : ''}Job restored by ${user?.name || 'Admin'} on ${format(new Date(), 'MMM d, yyyy')}`
        }
      });
      
      if (onUpdate) onUpdate();
      onClose();
    },
    onError: (error) => {
      setDebugInfo(prev => ({
        ...prev,
        lastError: error.message,
        attempts: prev.attempts + 1
      }));
    }
  });

  const [assignJobsToTeam, { loading: assignToTeamLoading }] = useMutation(ASSIGN_JOBS_TO_TEAM, {
    onCompleted: (data) => {
      setDebugInfo(prev => ({
        ...prev,
        lastResponse: JSON.stringify(data, null, 2),
        attempts: prev.attempts + 1
      }));
      
      if (data?.assignJobsToTeam) {
        // Use proper assignment cache invalidation
        invalidateAssignmentCaches(client);
        
        // Also evict the specific job from cache to force fresh data
        client.cache.evict({ 
          id: client.cache.identify({ __typename: 'Job', id: job.id }) 
        });
      }
      
      if (onUpdate) {
        setTimeout(() => {
          onUpdate();
        }, 100);
      }
      onClose();
    },
    onError: (error) => {
      setDebugInfo(prev => ({
        ...prev,
        lastError: error.message,
        attempts: prev.attempts + 1
      }));
    }
  });

  const [manuallyCancelJob, { loading: manuallyCancelJobLoading }] = useMutation(MANUALLY_CANCEL_JOB, {
    onCompleted: (data) => {
      setDebugInfo(prev => ({
        ...prev,
        lastResponse: JSON.stringify(data, null, 2),
        attempts: prev.attempts + 1
      }));
      
      // Use proper assignment cache invalidation
      invalidateAssignmentCaches(client);
      
      // Also evict the specific job from cache to force fresh data
      client.cache.evict({ 
        id: client.cache.identify({ __typename: 'Job', id: job.id }) 
      });
      
      if (onUpdate) {
        setTimeout(() => {
          onUpdate();
        }, 100);
      }
      onClose();
    },
    onError: (error) => {
      setDebugInfo(prev => ({
        ...prev,
        lastError: error.message,
        attempts: prev.attempts + 1
      }));
    }
  });

  const updateLoading = updateJobLoading || updateStatusLoading || updateDateLoading || assignToTeamLoading || manuallyCancelJobLoading || isProcessingDateUpdate;
  const assignmentDataLoading = loading || (shouldLoadData && (!isFormInitialized || jobDataLoading));

  // Load latest job data when it changes
  useEffect(() => {
    if (jobData?.job) {
      const latestJob = jobData.job;
      
      setFormData(prev =>
        mergeServerJobIntoForm(prev, latestJob, dirtyFieldsRef.current)
      );
      
      // CRITICAL FIX: Only update assignment type from fresh data if user hasn't manually changed it
      // This prevents overriding the user's selection when they're trying to reassign
      if (!userChangedAssignmentType) {
        if (latestJob.assignedTeamId) {
          setAssignmentType(ASSIGNMENT_TYPES.TEAM);
          setSelectedTeamId(String(latestJob.assignedTeamId));
        } else {
          setAssignmentType(ASSIGNMENT_TYPES.INDIVIDUAL);
          setSelectedTeamId('');
        }
      }
      
      // Mark form as initialized with fresh data
      setIsFormInitialized(true);
    }
  }, [jobData, job.notes, user?.role, userChangedAssignmentType]);

  // Form handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'serviceTypeId' && value !== job.serviceType?.id) {
      setUserFormData(prev => ({
        ...prev,
        [name]: value,
        hasServiceTypeOverride: true
      }));
    } else if (name === 'flipOverridden' && !checked) {
      setUserFormData(prev => ({
        ...prev,
        [name]: checked,
        flipOverrideValue: false
      }));
    } else if (name === 'scheduledDate') {
      // Automatically set hasDateOverride to true when user changes the date
      const originalDate = format(new Date(job.scheduledDate), "yyyy-MM-dd");
      const hasDateChanged = value !== originalDate;
      
      setUserFormData(prev => ({
        ...prev,
        [name]: value,
        hasDateOverride: hasDateChanged || prev.hasDateOverride // Keep existing override if already set
      }));
    } else if (name === 'startTime') {
      // Automatically set hasTimeOverride to true when user changes the time
      const originalTime = job.startTime || jobCheckoutTime || '';
      const hasTimeChanged = value !== originalTime;
      
      setUserFormData(prev => ({
        ...prev,
        [name]: value,
        hasTimeOverride: hasTimeChanged
      }));
    } else if (name === 'hasDateOverride') {
      // Handle manual checkbox toggle for date override
      setUserFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (name === 'hasTimeOverride') {
      // Handle manual checkbox toggle for time override
      setUserFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else {
      setUserFormData(prev => ({
        ...prev,
        [name]: type === 'checkbox' ? checked : value,
      }));
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    if (newValue !== TABS.RESERVATION) {
      reservationEdit.resetReservationState();
    }
  };

  // Handle assignment type change with proper cleanup
  const handleAssignmentTypeChange = (newType) => {
    setAssignmentType(newType);
    setUserChangedAssignmentType(true);
    
    // Clear the opposite mode's values to prevent conflicts
    if (newType === ASSIGNMENT_TYPES.INDIVIDUAL) {
      // Switching to individual - clear team selection
      setSelectedTeamId('');
    } else if (newType === ASSIGNMENT_TYPES.TEAM) {
      // Switching to team - clear individual selection
      setUserFormData(prev => ({
        ...prev,
        assignedToId: ''
      }));
    }
  };

  // Role-based permission checks
  const canCancelJobsCheck = () => canCancelRestoreJobs(user?.role);
  const canRestoreJobsCheck = () => canCancelRestoreJobs(user?.role);
  const canSeeFinancialFields = () => {
    const currentJob = jobData?.job || job;
    return canViewFinancials(user?.role) && (currentJob.serviceType?.showFinancialFields || false);
  };

  // Job action handlers
  const handleCancelJob = async () => {
    if (window.confirm("Are you sure you want to cancel this job? It will be moved to the canceled jobs section.")) {
      try {
        await manuallyCancelJob({
          variables: {
            id: job.id,
            reason: `Job canceled by ${user?.name || 'Admin'} on ${format(new Date(), 'MMM d, yyyy')}`
          }
        });
        if (onUpdate) onUpdate();
        onClose();
      } catch (error) {
        setDebugInfo(prev => ({
          ...prev,
          lastError: error.message
        }));
      }
    }
  };

  const handleRestoreJob = async () => {
    if (window.confirm("Are you sure you want to restore this job? It will be added back to active jobs.")) {
      try {
        await restoreJob({ variables: { id: job.id } });
      } catch (error) {
        setDebugInfo(prev => ({
          ...prev,
          lastError: error.message
        }));
      }
    }
  };

  // Copy job handler
  const handleCopyJob = () => {
    if (!job?.property?.id) {
      alert('Cannot copy job: Property information is missing');
      return;
    }

    const copyData = {
      property: job.property,
      propertyId: job.property.id,
      notes: job.notes || '',
      scheduledDate: job.scheduledDate ? format(new Date(job.scheduledDate), "yyyy-MM-dd") : null,
      // Include financial fields if they exist
      payout: job.payout !== null && job.payout !== undefined ? job.payout.toString() : '',
      billedAmount: job.billedAmount !== null && job.billedAmount !== undefined ? job.billedAmount.toString() : ''
    };

    onCopyJob?.(copyData);
    onClose();
  };

  // Handle custom flag changes
  const handleCustomFlagChange = () => {
    // Flags save independently. Refresh the list badges without touching the
    // Job entity or notifying parents that close their edit modal.
    try {
      client.refetchQueries({
        include: [GET_JOBS, GET_MY_SCHEDULE_JOBS]
      }).catch(error => {
        console.warn('Refetch warning (non-critical):', error);
      });
      
      setHistoryRefreshKey(key => key + 1);
    } catch (error) {
      console.error('Error invalidating cache:', error);
    }
  };

  // Form submission handler
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Prevent double submission
    if (updateLoading || restoreJobLoading || isProcessingDateUpdate) {
      return;
    }
    
    if (!user) {
      alert('You must be logged in to update jobs. Please refresh the page and log in again.');
      return;
    }
    
    if (!userHasRequiredRole) {
      alert(`You don't have permission to update jobs. Required roles: ${REQUIRED_ROLES.join(', ')}. Your role: ${user.role || 'None'}`);
      return;
    }
    

    
    try {
      // Check if date has changed and needs override
      const dateChanged = formData.scheduledDate !== format(new Date(job.scheduledDate), "yyyy-MM-dd");
      

      
      if (dateChanged || formData.hasDateOverride !== (job.hasDateOverride || false)) {
        // Prevent double processing
        if (isProcessingDateUpdate) {
          return;
        }
        
        setIsProcessingDateUpdate(true);
        
        const scheduledDateParts = formData.scheduledDate.split('-');
        const year = parseInt(scheduledDateParts[0]);
        const month = parseInt(scheduledDateParts[1]) - 1;
        const day = parseInt(scheduledDateParts[2]);
        const scheduledDate = new Date(year, month, day, 12, 0, 0);
        
        // If date changed, ensure hasDateOverride is set to true
        const hasDateOverride = dateChanged ? true : formData.hasDateOverride;
        
        await updateJobScheduledDate({
          variables: {
            id: job.id,
            scheduledDate: scheduledDate.toISOString(),
            hasDateOverride: hasDateOverride
          }
        });
        return;
      }
      
      // Prepare financial fields
      const financialFields = {};
      if (formData.showFinancialFields) {
        if (formData.payout !== '') {
          financialFields.payout = parseFloat(formData.payout);
        }
        if (formData.billedAmount !== '') {
          financialFields.billedAmount = parseFloat(formData.billedAmount);
        }
      }
      
      // Check if assignment has changed
      const assignmentChanged = 
        (assignmentType === ASSIGNMENT_TYPES.TEAM && (
          (!job.assignedTeamId && selectedTeamId !== '') || // No team -> has team
          (job.assignedTeamId && selectedTeamId === '') || // Has team -> no team (unassignment)
          (job.assignedTeamId && selectedTeamId !== '' && selectedTeamId !== String(job.assignedTeamId)) // Different team
        )) ||
        (assignmentType === ASSIGNMENT_TYPES.INDIVIDUAL && (
          job.assignedTeamId || // Has team -> switching to individual
          (formData.assignedToId === '' && job.assignedTo?.id) || // Unassigning from a user
          (formData.assignedToId !== '' && formData.assignedToId !== (job.assignedTo?.id ? String(job.assignedTo.id) : '')) || // Different user
          (!job.assignedTo?.id && formData.assignedToId !== '') // Unassigned -> assigned to user
        ));

      // Handle team assignment to team (not unassignment) with early return
      if (assignmentType === ASSIGNMENT_TYPES.TEAM && assignmentChanged && selectedTeamId && selectedTeamId !== '') {
        // Assign to team (selectedTeamId has a value)
        await assignJobsToTeam({
          variables: {
            jobIds: [job.id],
            teamId: parseInt(selectedTeamId, 10)
          }
        });
        return;
      }

      // Handle other job updates
      if (
        formData.hasTimeOverride !== (job.hasTimeOverride || false) ||
        (formData.hasTimeOverride && formData.startTime !== (job.startTime || '')) ||
        formData.showFinancialFields ||
        assignmentChanged ||
        formData.serviceTypeId !== (job.serviceType?.id || '') ||
        formData.hasServiceTypeOverride !== (job.hasServiceTypeOverride || false) ||
        formData.flipOverridden !== (job.flipOverridden || false) ||
        (formData.flipOverridden && formData.flipOverrideValue !== job.flipOverrideValue)
      ) {
        const startTimeToSend = formData.hasTimeOverride ? formData.startTime : null;
        const assignedToIdValue = assignmentType === ASSIGNMENT_TYPES.INDIVIDUAL
          ? (formData.assignedToId && formData.assignedToId !== '' ? parseInt(formData.assignedToId, 10) : null)
          : assignmentType === ASSIGNMENT_TYPES.TEAM && (!selectedTeamId || selectedTeamId === '')
            ? null  // Team unassignment - clear assignedToId
            : undefined; // Team assignment handled above, don't modify assignedToId here
        
        const assignedTeamIdValue = assignmentType === ASSIGNMENT_TYPES.TEAM && (!selectedTeamId || selectedTeamId === '')
          ? null  // Team unassignment - clear assignedTeamId
          : assignmentType === ASSIGNMENT_TYPES.INDIVIDUAL
            ? null  // Individual assignment - clear any existing team assignment
            : undefined; // Team assignment handled above, don't modify assignedTeamId here
        
        const serviceTypeFields = {};
        if (formData.serviceTypeId && formData.serviceTypeId !== (job.serviceType?.id || '')) {
          serviceTypeFields.serviceTypeId = formData.serviceTypeId;
          serviceTypeFields.hasServiceTypeOverride = formData.hasServiceTypeOverride;
        } else if (formData.hasServiceTypeOverride !== (job.hasServiceTypeOverride || false)) {
          serviceTypeFields.hasServiceTypeOverride = formData.hasServiceTypeOverride;
        }

        const flipOverrideFields = {};
        if (formData.flipOverridden !== (job.flipOverridden || false)) {
          flipOverrideFields.flipOverridden = formData.flipOverridden;
          if (formData.flipOverridden) {
            flipOverrideFields.flipOverrideValue = formData.flipOverrideValue;
          }
        } else if (formData.flipOverridden && formData.flipOverrideValue !== job.flipOverrideValue) {
          flipOverrideFields.flipOverrideValue = formData.flipOverrideValue;
        }
        
        const updateJobInput = {
          id: job.id,
          hasDateOverride: formData.hasDateOverride,
          hasTimeOverride: formData.hasTimeOverride,
          startTime: startTimeToSend,
          ...(assignedToIdValue !== undefined && { assignedToId: assignedToIdValue }),
          ...(assignedTeamIdValue !== undefined && { assignedTeamId: assignedTeamIdValue }),
          ...(assignmentChanged && { hasAssignmentOverride: true }),
          notes: formData.notes,
          ...serviceTypeFields,
          ...flipOverrideFields,
          ...financialFields
        };
        
        await updateJob({
          variables: {
            id: job.id,
            input: updateJobInput
          }
        });
        
        if (formData.status !== job.status) {
          // If status is being changed to CANCELLED, use manual cancellation
          if (formData.status === 'CANCELLED') {
            await manuallyCancelJob({
              variables: {
                id: job.id,
                reason: formData.notes || `Job manually cancelled by ${user?.name || 'Admin'} on ${format(new Date(), 'MMM d, yyyy')}`
              }
            });
          } else {
            await updateJobStatus({
              variables: {
                id: job.id,
                status: formData.status,
                notes: formData.notes
              }
            });
          }
        } else {
          // If no status update needed, close the modal manually
          if (onUpdate) {
            setTimeout(() => {
              onUpdate();
            }, 100);
          }
          onClose();
        }
      } else {
        await updateJobStatus({
          variables: {
            id: job.id,
            status: formData.status,
            notes: formData.notes
          },
        });
      }
    } catch (error) {
      setDebugInfo(prev => ({
        ...prev,
        lastError: error.message
      }));
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-2 sm:p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] flex flex-col">
        <div className="overflow-y-scroll" style={{ maxHeight: 'calc(90vh - 40px)' }}>
          {/* Enhanced Header */}
          <div className="px-3 py-4 sm:px-4 sm:py-5 md:px-6 rounded-t-lg md:py-6 border-b border-sky-800 bg-sky-900">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 sm:space-x-3 mb-3">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 sm:w-10 sm:h-10 bg-sky-700 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </div>
                  </div>
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-white tracking-tight">
                      Edit Job
                    </h1>
                    <p className="text-xs sm:text-sm text-gray-100 mt-1">
                      Modify job details and reservation settings
                    </p>
                  </div>
                </div>
                
                {job.property && (
                  <div className="mt-3">
                    <h2 className="text-lg sm:text-xl font-semibold text-white">
                      {job.property.name}
                    </h2>
                  </div>
                )}
              </div>
              
              {/* Service Type Badge and Copy Button - Top Right */}
              <div className="flex-shrink-0 ml-3 sm:ml-4 md:ml-6">
                <div className="text-right space-y-2">
                  {job.serviceType && (
                    <div>
                      <span className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-semibold bg-white text-sky-800 border border-sky-200 shadow">
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {job.serviceType.title}
                      </span>
                    </div>
                  )}
                  
                  {/* Copy Job Button */}
                  {onCopyJob && job.property && (
                    <div>
                      <button
                        type="button"
                        onClick={handleCopyJob}
                        className="inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium bg-sky-700 text-white hover:bg-sky-600 border border-sky-600 shadow transition-colors duration-200"
                        title="Copy this job to create a new job for the same property"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                        Copy Job
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Enhanced Tabs */}
          <div className="bg-white">
            <Tabs 
              value={activeTab} 
              onChange={handleTabChange}
              variant="fullWidth"
              indicatorColor="primary"
              textColor="primary"
              sx={{
                minHeight: '48px',
                '@media (min-width: 640px)': {
                  minHeight: '52px',
                },
                '& .MuiTabs-root': {
                  backgroundColor: 'transparent',
                },
                '& .MuiTab-root': {
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  minHeight: '48px',
                  backgroundColor: '#e2e8f0',
                  color: '#64748b',
                  border: '1px solid #e2e8f0',
                  borderBottom: '1px solid #e2e8f0',
                  marginRight: '2px',
                  borderRadius: '8px 8px 0 0',
                  position: 'relative',
                  zIndex: 0,
                  '&:hover': {
                    backgroundColor: '#cbd5e1',
                    color: '#475569',
                  },
                  '@media (min-width: 640px)': {
                    fontSize: '0.875rem',
                    minHeight: '52px',
                  },
                },
                '& .Mui-selected': {
                  backgroundColor: '#f8fafc !important',
                  color: '#4f46e5 !important',
                  borderColor: '#e2e8f0',
                  borderBottom: 'none !important',
                  zIndex: 2,
                  position: 'relative',
                  '&::after': {
                    content: '""',
                    position: 'absolute',
                    bottom: '-1px',
                    left: '-1px',
                    right: '-1px',
                    height: '2px',
                    backgroundColor: '#f8fafc',
                    zIndex: 3,
                  }
                },
                '& .MuiTabs-indicator': {
                  display: 'none',
                },
                '& .MuiTabs-flexContainer': {
                  paddingLeft: '12px',
                  paddingRight: '12px',
                  paddingTop: '8px',
                  '@media (min-width: 640px)': {
                    paddingLeft: '16px',
                    paddingRight: '16px',
                  },
                  '@media (min-width: 768px)': {
                    paddingLeft: '24px',
                    paddingRight: '24px',
                  },
                }
              }}
            >
              <Tab 
                value={TABS.JOB} 
                label="Edit Job"
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                }
                iconPosition="start"
              />
              <Tab 
                value={TABS.RESERVATION} 
                label="Edit Reservation"
                disabled={!job?.reservation?.id}
                icon={
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                }
                iconPosition="start"
              />
            </Tabs>
          </div>

          {/* Tab Content Container */}
          <div className="bg-slate-50 border border-gray-200 border-t-0 mx-3 sm:mx-4 md:mx-6 rounded-b-lg shadow-sm">
            {/* Show form immediately with job prop data, fresh data will update it when loaded */}
            {jobDataError ? (
              <div className="px-3 py-8 sm:px-4 md:px-6 text-center">
                <div className="text-red-600">
                  Error loading job details: {jobDataError.message}
                </div>
                <button 
                  onClick={() => window.location.reload()} 
                  className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                  Reload Page
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-3 py-4 sm:px-4 sm:py-5 md:px-6 md:py-6">
              {/* Show loading indicator when fresh data is being loaded */}
              {jobDataLoading && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <svg className="animate-spin h-4 w-4 text-blue-600 mr-2" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm text-blue-700">Loading latest job data...</span>
                  </div>
                </div>
              )}
              
              {activeTab === TABS.JOB ? (
              <JobEditForm 
                job={jobData?.job || job}
                formData={formData}
                setFormData={setUserFormData}
                handleChange={handleChange}
                assignmentType={assignmentType}
                setAssignmentType={handleAssignmentTypeChange}
                selectedTeamId={selectedTeamId}
                setSelectedTeamId={setSelectedTeamId}
                sortedActiveUsers={sortedActiveUsers}
                teamsData={teamsData}
                assignmentDataLoading={assignmentDataLoading}
                usersLoading={usersLoading}
                teamsLoading={teamsLoading}
                availableServiceTypes={availableServiceTypes}
                canSeeFinancialFields={canSeeFinancialFields}
                user={user}
                jobCheckoutTime={jobCheckoutTime}
                displayTime={displayTime}
                previousReservation={previousReservation}
                handleCancelJob={handleCancelJob}
                canCancelJob={canCancelJobsCheck()}
                cancelJobLoading={manuallyCancelJobLoading}
                onCustomFlagChange={handleCustomFlagChange}
                historyRefreshKey={historyRefreshKey}
              />
            ) : (
              <ReservationEditForm 
                reservationError={reservationEdit.reservationError}
                reservationView={reservationEdit.reservationView}
                setReservationView={reservationEdit.setReservationView}
                previousReservation={previousReservation}
                nextReservation={nextReservation}
                currentReservationData={reservationEdit.currentReservationData}
                editingField={reservationEdit.editingField}
                reservationEditData={reservationEdit.reservationEditData}
                setReservationEditData={reservationEdit.setReservationEditData}
                onEditField={reservationEdit.handleEditField}
                onSaveEdit={reservationEdit.handleSaveEdit}
                onCancelEdit={reservationEdit.handleCancelEdit}
                affectedJobsCount={affectedJobsCount}
                job={job}
              />
            )}

              {/* Action buttons */}
              <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-gray-200 bg-slate-100 -mx-3 sm:-mx-4 md:-mx-6 px-3 sm:px-4 md:px-6 pb-4 sm:pb-6">
              <div className="sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                <button
                  type={activeTab === TABS.RESERVATION ? 'button' : 'submit'}
                  onClick={activeTab === TABS.RESERVATION ? onClose : undefined}
                  disabled={updateLoading || restoreJobLoading || isProcessingDateUpdate}
                  className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 text-base font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 sm:col-start-2 sm:text-sm disabled:opacity-50 ${
                    activeTab === TABS.RESERVATION ? 'bg-gray-600 hover:bg-gray-700 focus:ring-gray-500' : 'bg-sky-600 hover:bg-sky-700 focus:ring-sky-500'
                  }`}
                >
                  {getSubmitButtonText(activeTab, updateLoading)}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sky-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                >
                  Cancel
                </button>
              </div>
              
              {/* Secondary actions - only show in job tab */}
              {activeTab === TABS.JOB && (
                <>

                  {(job.isDeleted && canRestoreJobsCheck()) && (
                    <div className="mt-4 text-center">
                      <button
                        type="button"
                        onClick={handleRestoreJob}
                        disabled={restoreJobLoading}
                        className="inline-flex justify-center rounded-md border border-green-300 shadow-sm px-4 py-2 bg-white text-sm font-medium text-green-700 hover:bg-green-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
                      >
                        {restoreJobLoading ? 'Restoring Job...' : 'Restore Job'}
                      </button>
                    </div>
                  )}
                </>
              )}
              </div>
            </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditJobModal;
